import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import {
  MoreHorizontal,
  Users,
  Users2,
  FileText,
  FolderOpen,
} from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import {
  ListItemCardProps,
  ListItemCardSubtitle,
  ListItemCardMetrics,
  ListItemAction,
} from "./types";
import {
  CardRow,
  CheckboxArea,
  IconArea,
  InfoArea,
  TitleRow,
  MetricsRow,
  MetricItem,
  MetricClickable,
  MetricSeparator,
  MetricsWrapper,
  RightArea,
  BadgesRow,
  ActionsRow,
  ActionBtn,
  IconActionBtn,
  MoreBtn,
  PublishBtn,
} from "./style";

const ListItemCardCst: React.FC<ListItemCardProps> = ({
  icon,
  title,
  subtitle,
  metrics,
  badges,
  actions,
  moreActions,
  selectable = false,
  selected = false,
  onSelect,
  onClick,
  onMetricClick,
  isPublished,
  onPublish,
  onCreateEdition,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("list-item-card");

  const moreActionGroups: ListItemAction[][] = useMemo(() => {
    if (!moreActions || moreActions.length === 0) return [];
    const isGrouped = Array.isArray(moreActions[0]);
    return (isGrouped ? moreActions : [moreActions]) as ListItemAction[][];
  }, [moreActions]);

  const showPublishBtn = isPublished === false && !!onPublish;
  const showCreateEditionBtn = !!onCreateEdition;

  const buildSubtitle = (sub: ListItemCardSubtitle): string => {
    const parts: string[] = [];
    if (sub.id !== undefined) parts.push(`ID: ${sub.id}`);
    if (sub.activityType) parts.push(sub.activityType);
    if (sub.status) parts.push(sub.status);
    if (sub.mode) parts.push(sub.mode);
    return parts.join(" | ");
  };

  const metricsRows = useMemo(() => {
    if (!metrics) return [];
    const row1: {
      key: keyof ListItemCardMetrics;
      icon: React.ReactNode;
      label: string;
    }[] = [];
    const row2: {
      key: keyof ListItemCardMetrics;
      icon: React.ReactNode;
      label: string;
    }[] = [];

    if (metrics.enrolledUsers !== undefined)
      row1.push({
        key: "enrolledUsers",
        icon: <Users size={14} />,
        label: t("metrics.enrolled-users", { count: metrics.enrolledUsers }),
      });
    if (metrics.enrolledGroups !== undefined)
      row1.push({
        key: "enrolledGroups",
        icon: <Users2 size={14} />,
        label: t("metrics.enrolled-groups", { count: metrics.enrolledGroups }),
      });
    if (metrics.initiatives !== undefined)
      row2.push({
        key: "initiatives",
        icon: <FileText size={14} />,
        label: t("metrics.initiatives", { count: metrics.initiatives }),
      });
    if (metrics.containedActivities !== undefined)
      row2.push({
        key: "containedActivities",
        icon: <FolderOpen size={14} />,
        label: t("metrics.contained-activities", {
          count: metrics.containedActivities,
        }),
      });
    if (metrics.courses !== undefined)
      row2.push({
        key: "courses",
        icon: <FolderOpen size={14} />,
        label: t("metrics.courses", { count: metrics.courses }),
      });
    if (metrics.containedCourses !== undefined)
      row2.push({
        key: "containedCourses",
        icon: <FolderOpen size={14} />,
        label: t("metrics.contained-courses", {
          count: metrics.containedCourses,
        }),
      });
    if (metrics.paths !== undefined)
      row2.push({
        key: "paths",
        icon: <FolderOpen size={14} />,
        label: t("metrics.paths", { count: metrics.paths }),
      });

    return [row1, row2].filter((row) => row.length > 0);
  }, [metrics, t]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMoreClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (actionFn: () => void) => {
    handleMenuClose();
    actionFn();
  };

  return (
    <CardRow onClick={onClick} sx={{ cursor: onClick ? "pointer" : "default" }}>
      {selectable && (
        <CheckboxArea>
          <Checkbox
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect?.(!selected);
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: theme.palette.divider,
              "&.Mui-checked": {
                color: theme.palette.primary.main,
              },
              padding: "4px",
            }}
          />
        </CheckboxArea>
      )}

      {icon && <IconArea>{icon}</IconArea>}

      <InfoArea>
        <TitleRow>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.3,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
        </TitleRow>

        {subtitle && (
          <Typography
            sx={{
              fontSize: "12px",
              color: theme.palette.text.tertiary,
              lineHeight: 1.3,
            }}
          >
            {buildSubtitle(subtitle)}
          </Typography>
        )}

        {metricsRows.length > 0 && (
          <MetricsWrapper>
            {metricsRows.map((row, rowIndex) => (
              <MetricsRow key={rowIndex}>
                {row.map((metric, metricIndex) => (
                  <React.Fragment key={metric.key}>
                    {metricIndex > 0 && <MetricSeparator />}
                    <MetricItem>
                      {metric.icon}
                      {onMetricClick ? (
                        <MetricClickable
                          onClick={(e) => {
                            e.stopPropagation();
                            onMetricClick(metric.key);
                          }}
                        >
                          {metric.label}
                        </MetricClickable>
                      ) : (
                        <span>{metric.label}</span>
                      )}
                    </MetricItem>
                  </React.Fragment>
                ))}
              </MetricsRow>
            ))}
          </MetricsWrapper>
        )}
      </InfoArea>

      <RightArea>
        {badges && badges.length > 0 && (
          <BadgesRow>
            {badges.map((badge, index) => (
              <BadgeBoxCst
                key={index}
                variant={badge.variant}
                icon={badge.icon}
                sx={badge.sx}
              >
                {badge.label}
              </BadgeBoxCst>
            ))}
          </BadgesRow>
        )}

        {(actions ||
          moreActionGroups.length > 0 ||
          showPublishBtn ||
          showCreateEditionBtn) && (
          <ActionsRow>
            {/* TODO: da implementare quando avremo le bozze */}
            {/* {showPublishBtn && (
              <PublishBtn
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onPublish?.();
                }}
              >
                {t("publish-button")}
              </PublishBtn>
            )} */}

            {showCreateEditionBtn && (
              <PublishBtn
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateEdition?.();
                }}
              >
                {t("create-edition-button")}
              </PublishBtn>
            )}

            {actions?.map((action, index) =>
              action.iconOnly ? (
                <IconActionBtn
                  key={index}
                  disabled={action.disabled}
                  aria-label={action.label}
                  title={action.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                >
                  {action.icon}
                </IconActionBtn>
              ) : (
                <ActionBtn
                  key={index}
                  variant={action.variant || "outlined"}
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                >
                  {action.label}
                </ActionBtn>
              ),
            )}

            {moreActionGroups.length > 0 && (
              <>
                <MoreBtn onClick={handleMoreClick}>
                  <MoreHorizontal size={18} />
                </MoreBtn>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  onClick={(e) => e.stopPropagation()}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  {moreActionGroups.map((group, groupIndex) => [
                    groupIndex > 0 && (
                      <Divider
                        key={`divider-${groupIndex}`}
                        sx={{ my: "4px" }}
                      />
                    ),
                    ...group.map((action, actionIndex) => (
                      <MenuItem
                        key={`${groupIndex}-${actionIndex}`}
                        disabled={action.disabled}
                        onClick={() => handleMenuAction(action.onClick)}
                        sx={{
                          fontSize: "13px",
                          color: theme.palette.text.primary,
                          gap: "10px",
                        }}
                      >
                        {action.icon && (
                          <ListItemIcon
                            sx={{
                              minWidth: "auto !important",
                              color: theme.palette.text.primary,
                            }}
                          >
                            {action.icon}
                          </ListItemIcon>
                        )}
                        {action.label}
                      </MenuItem>
                    )),
                  ])}
                </Menu>
              </>
            )}
          </ActionsRow>
        )}
      </RightArea>
    </CardRow>
  );
};

export default ListItemCardCst;
